import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Search, Printer, Download, Settings, ChevronDown, ChevronLeft, MoreHorizontal } from 'lucide-react';
import { money, fmtDate } from '../money';

// OL Finance — Accounting menu pages (Chart of accounts, Register, Rules). Light readable
// tables (white card, slate text). No hardcoded account names/types/ids — everything from DB;
// the only literals are UI labels and the canonical account-type display order.
const TYPE_ORDER = [
  'Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset', 'Other Asset',
  'Accounts Payable', 'Credit Card', 'Other Current Liability', 'Long Term Liability',
  'Equity', 'Income', 'Cost of Goods Sold', 'Expense', 'Other Income', 'Other Expense',
];
const BALANCE_SHEET = new Set(TYPE_ORDER.slice(0, 10));  // Bank … Equity
const PAGE_SIZE = 75;
const card = (isDark) => isDark ? 'bg-white text-slate-800 border-slate-200' : 'bg-white text-slate-800 border-slate-200';

function typeRank(t) { const i = TYPE_ORDER.indexOf(t); return i < 0 ? 99 : i; }

export function ChartOfAccounts({ base, isDark, go, onOpenReport, onOpenRegister }) {
  const [accts, setAccts] = useState([]);
  const [q, setQ] = useState('');
  const [typeF, setTypeF] = useState('All');
  const [page, setPage] = useState(0);
  const [sel, setSel] = useState({});
  const [gear, setGear] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [newMenu, setNewMenu] = useState(false);

  const load = () => axios.get(`${base}/accounts`).then((r) => setAccts(r.data?.accounts || [])).catch(() => setAccts([]));
  useEffect(() => { load(); }, [base]);

  // Bank balance = the saved statement balance from GET /accounts (NOT the feed-row sum).
  const hasStmt = (a) => a.statement_balance != null;

  const types = useMemo(() => Array.from(new Set(accts.map((a) => a.type))).sort((a, b) => typeRank(a) - typeRank(b)), [accts]);

  const ordered = useMemo(() => {
    const byId = {}; accts.forEach((a) => { byId[a.account_id] = a; });
    const depth = (a) => (a.full_name || a.name || '').split(':').length - 1;
    const sortKey = (a) => [typeRank(a.type), (a.full_name || a.name || '').toLowerCase()];
    return [...accts].sort((a, b) => {
      const ka = sortKey(a), kb = sortKey(b);
      return ka[0] - kb[0] || (ka[1] < kb[1] ? -1 : ka[1] > kb[1] ? 1 : 0);
    }).map((a) => ({ ...a, _depth: depth(a) }));
  }, [accts]);

  const filtered = ordered
    .filter((a) => typeF === 'All' || a.type === typeF)
    .filter((a) => { const s = q.toLowerCase(); return !s || (a.full_name || a.name || '').toLowerCase().includes(s) || String(a.number || '').includes(s); });

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const cur = Math.min(page, pageCount - 1);
  const shown = filtered.slice(cur * PAGE_SIZE, cur * PAGE_SIZE + PAGE_SIZE);
  useEffect(() => { setPage(0); }, [q, typeF]);

  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const makeInactive = async (ids) => {
    for (const id of ids) { await axios.put(`${base}/accounts/${id}`, { is_active: false }).catch(() => {}); }
    setSel({}); load();
  };
  const isBS = (a) => BALANCE_SHEET.has(a.type);

  const exportCsv = () => {
    const head = ['Name', 'Account type', 'Detail type', 'Book balance', 'Bank balance'];
    const body = filtered.map((a) => [a.full_name || a.name, a.type, a.detail_type || '',
      a.balance == null ? '' : (a.balance / 100).toFixed(2),
      hasStmt(a) ? (a.statement_balance / 100).toFixed(2) : ''].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[head.join(','), ...body].join('\n')], { type: 'text/csv' });
    const u = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = u; a.download = 'chart_of_accounts.csv'; a.click(); URL.revokeObjectURL(u);
  };

  return (
    <div className="space-y-3 text-slate-800" data-testid="coa-page">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold text-slate-800">Chart of accounts</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => onOpenReport('account_list')} data-testid="coa-run-report">Run report</Button>
          <div className="relative">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setNewMenu((v) => !v)} data-testid="coa-new"><span className="mr-1">New account</span><ChevronDown className="h-4 w-4" /></Button>
            {newMenu && (
              <div className="absolute right-0 top-10 z-30 w-40 rounded-lg border bg-white border-slate-200 shadow-xl text-slate-800">
                <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setNewMenu(false); go('form', 'account'); }} data-testid="coa-new-account">New account</button>
                <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setNewMenu(false); go('importdata'); }} data-testid="coa-import">Import</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs flex-1"><Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
          <input className="w-full h-9 pl-8 pr-2 rounded border border-slate-300 bg-white text-sm text-slate-800" placeholder="Filter by name or number" value={q} onChange={(e) => setQ(e.target.value)} data-testid="coa-filter" /></div>
        <select className="h-9 rounded border border-slate-300 bg-white px-2 text-sm text-slate-800" value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="coa-type-filter">
          <option value="All">All</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {selIds.length > 0 && (
          <Button variant="outline" onClick={() => makeInactive(selIds)} data-testid="coa-batch-inactive">Batch edit · Make inactive ({selIds.length})</Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="icon" title="Export" onClick={exportCsv} data-testid="coa-export"><Download className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" title="Print" onClick={() => window.print()} data-testid="coa-print"><Printer className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" title="Settings" onClick={() => setGear((v) => !v)} data-testid="coa-settings"><Settings className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className={`rounded-lg border overflow-hidden ${card(isDark)}`}>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200">
            <th className="p-2.5 w-8"></th>
            <th className="p-2.5">Name</th><th className="p-2.5">Account type</th><th className="p-2.5">Detail type</th>
            <th className="p-2.5 text-right">Book balance</th><th className="p-2.5 text-right">Bank balance</th><th className="p-2.5 w-28">Action</th>
          </tr></thead>
          <tbody>
            {shown.map((a, i) => (
              <tr key={a.account_id} className="border-b border-slate-100 hover:bg-emerald-500/5" data-testid={`coa-row-${i}`}>
                <td className="p-2.5"><input type="checkbox" checked={!!sel[a.account_id]} onChange={(e) => setSel((s) => ({ ...s, [a.account_id]: e.target.checked }))} data-testid={`coa-check-${i}`} /></td>
                <td className="p-2.5">
                  <span style={{ paddingLeft: `${a._depth * 16}px` }}>{a._depth > 0 ? '↳ ' : ''}{a.name}{a.is_active === false ? ' (inactive)' : ''}</span>
                </td>
                <td className="p-2.5">{a.type}</td>
                <td className="p-2.5 text-slate-500">{a.detail_type || ''}</td>
                <td className="p-2.5 text-right tabular-nums">{isBS(a) && a.balance != null ? money(a.balance) : ''}</td>
                <td className="p-2.5 text-right tabular-nums">{hasStmt(a) ? money(a.statement_balance) : ''}</td>
                <td className="p-2.5 relative">
                  <div className="flex items-center gap-1">
                    {isBS(a)
                      ? <button className="text-emerald-700 hover:underline" onClick={() => onOpenRegister(a)} data-testid={`coa-register-${i}`}>View register</button>
                      : <button className="text-emerald-700 hover:underline" onClick={() => onOpenReport('transaction_detail_by_account', a.account_id)} data-testid={`coa-report-${i}`}>Run report</button>}
                    <button onClick={() => setOpenRow(openRow === i ? null : i)} data-testid={`coa-more-${i}`}><ChevronDown className="h-4 w-4 text-slate-400" /></button>
                  </div>
                  {openRow === i && (
                    <div className="absolute right-2 top-8 z-30 w-36 rounded-lg border bg-white border-slate-200 shadow-xl text-left">
                      <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setOpenRow(null); go('form', 'account'); }}>Edit</button>
                      <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setOpenRow(null); makeInactive([a.account_id]); }}>Make inactive</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-3 text-slate-500" data-testid="coa-empty">No accounts match.</td></tr>}
          </tbody>
        </table>
        {pageCount > 1 && (
          <div className="flex items-center justify-between px-3 py-2 text-sm border-t border-slate-200" data-testid="coa-pager">
            <span className="text-slate-500">Showing accounts {cur * PAGE_SIZE + 1} to {Math.min((cur + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={cur === 0} onClick={() => setPage(cur - 1)} data-testid="coa-prev">Previous</Button>
              <Button size="sm" variant="outline" disabled={cur >= pageCount - 1} onClick={() => setPage(cur + 1)} data-testid="coa-next">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function Register({ base, isDark, account, onBack, onOpenTxn }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const SIZE = 50;
  useEffect(() => {
    let cancel = false;
    (async () => {
      const all = [];
      for (let p = 0; p < 200; p++) {
        const r = await axios.get(`${base}/bank-register/${account.account_id}?sort=date&page=${p}`).catch(() => null);
        const rows = r?.data?.rows || [];
        all.push(...rows);
        if (rows.length < 200) break;
      }
      if (!cancel) setData({ rows: all });
    })();
    return () => { cancel = true; };
  }, [base, account]);

  const isCC = account.type === 'Credit Card';
  const inc = isCC ? 'Payment' : 'Deposit';
  const dec = isCC ? 'Charge' : 'Payment';
  const rows = (data?.rows || []);
  const ending = account.balance != null ? account.balance : (rows.length ? rows[rows.length - 1].balance : 0);
  const filt = rows.filter((r) => {
    const s = q.toLowerCase();
    const okq = !s || [r.payee, r.memo, r.account, r.type].some((x) => String(x || '').toLowerCase().includes(s));
    const okf = !from || (r.date || '') >= from;
    const okt = !to || (r.date || '') <= to;
    return okq && okf && okt;
  }).slice().reverse();  // newest first
  const pageCount = Math.max(1, Math.ceil(filt.length / SIZE));
  const cur = Math.min(page, pageCount - 1);
  const shown = filt.slice(cur * SIZE, cur * SIZE + SIZE);
  useEffect(() => { setPage(0); }, [q, from, to]);

  return (
    <div className="space-y-3 text-slate-800" data-testid="register-page">
      <button className="flex items-center text-sm text-emerald-700 hover:underline" onClick={onBack} data-testid="register-back"><ChevronLeft className="h-4 w-4" />Back to Chart of accounts</button>
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{account.full_name || account.name}</h2>
          {data?.reconciled_through && <div className="text-xs text-emerald-700" data-testid="register-reconciled-through">Reconciled through {fmtDate(data.reconciled_through.statement_date)}</div>}
        </div>
        <div className="text-right"><div className="text-xs text-slate-500">Ending balance</div><div className="text-lg font-semibold tabular-nums">{money(ending)}</div></div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs flex-1"><Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
          <input className="w-full h-9 pl-8 pr-2 rounded border border-slate-300 bg-white text-sm" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} data-testid="register-search" /></div>
        <input type="date" className="h-9 rounded border border-slate-300 bg-white px-2 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="register-from" />
        <input type="date" className="h-9 rounded border border-slate-300 bg-white px-2 text-sm" value={to} onChange={(e) => setTo(e.target.value)} data-testid="register-to" />
      </div>
      <div className={`rounded-lg border overflow-hidden ${card(isDark)}`}>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200">
            <th className="p-2.5">Date</th><th className="p-2.5">Ref no.</th><th className="p-2.5">Type</th><th className="p-2.5">Payee</th>
            <th className="p-2.5">Account</th><th className="p-2.5">Memo</th><th className="p-2.5 text-right">{dec}</th><th className="p-2.5 text-right">{inc}</th>
            <th className="p-2.5 text-center">✓</th><th className="p-2.5 text-right">Balance</th>
          </tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={r.gl_id || i} className="border-b border-slate-100 hover:bg-emerald-500/5 cursor-pointer" onClick={() => onOpenTxn && onOpenTxn(r)} data-testid={`register-row-${i}`}>
                <td className="p-2.5">{fmtDate(r.date)}</td><td className="p-2.5">{r.number || ''}</td><td className="p-2.5">{r.type || ''}</td>
                <td className="p-2.5">{r.payee || ''}</td><td className="p-2.5">{r.account || ''}</td><td className="p-2.5 text-slate-500">{r.memo || ''}</td>
                <td className="p-2.5 text-right tabular-nums">{r.decrease ? money(r.decrease) : ''}</td>
                <td className="p-2.5 text-right tabular-nums">{r.increase ? money(r.increase) : ''}</td>
                <td className="p-2.5 text-center">{r.cleared}</td>
                <td className="p-2.5 text-right tabular-nums">{money(r.balance)}</td>
              </tr>
            ))}
            {filt.length === 0 && <tr><td colSpan={10} className="p-3 text-slate-500" data-testid="register-empty">No transactions.</td></tr>}
          </tbody>
        </table>
        {pageCount > 1 && (
          <div className="flex items-center justify-between px-3 py-2 text-sm border-t border-slate-200" data-testid="register-pager">
            <span className="text-slate-500">Showing {cur * SIZE + 1} to {Math.min((cur + 1) * SIZE, filt.length)} of {filt.length}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={cur === 0} onClick={() => setPage(cur - 1)} data-testid="register-prev">Previous</Button>
              <Button size="sm" variant="outline" disabled={cur >= pageCount - 1} onClick={() => setPage(cur + 1)} data-testid="register-next">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
